import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useAuth } from "../../services/AuthContext.js";
import { logCheckoutEvent } from "../../services/user_log.js";
import {
  TextField,
  MenuItem,
  Grid,
  InputAdornment,
  Radio,
} from "@mui/material";
import { CreditCard, User, Lock, QrCode } from "lucide-react";
import { notify } from "../../services/notify.js";
import {
  createPixSubscription1,
  createPixSubscription2,
  createCardSubscription,
  createInitiateCheckoutLead,
} from "../../services/subscriptionService";
import { trackMetaPurchase } from "../../utils/metaAds";
import {
  onlyDigits,
  parseExpiry,
  validatePersonalFields,
  validateCheckout,
  isValidCPF,
} from "./validation.js";
import OfferSummary from "./offer_summary.js";
import PixScreen from "./pix_screen.js";
import SuccessScreen from "./success_screen.js";
import { normalizeOfferPeriod } from "../../config/subscription.js";
import "./checkout_single.css";

const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

const useQuery = () => new URLSearchParams(useLocation().search);

const CACHED_OFFERS = {
  DEFAULT: {
    code: "DEFAULT",
    headline: "Torne-se aluno do site Dom\u00ednio El\u00e9trico!",
    id: 1,
    interest_rate: "0.0259",
    offer_type: "DEFAULT",
    period: "QUINQUENNIAL",
    prices: {
      base: "397",
      credit_card: {
        1: "397.00", 2: "206.24", 3: "139.25", 4: "105.76",
        5: "85.67", 6: "72.29", 7: "62.74", 8: "55.58",
        9: "50.02", 10: "45.57", 11: "41.94", 12: "38.91",
      },
      pix: "397",
    },
  },
  DEFAULT2: {
    code: "DEFAULT2",
    headline: "Torne-se aluno do site Dom\u00ednio El\u00e9trico!",
    id: 1,
    interest_rate: "0.0259",
    offer_type: "DEFAULT",
    period: "QUINQUENNIAL",
    prices: {
      base: "197",
      credit_card: {
        1: "197.00", 2: "102.34", 3: "69.10", 4: "52.48",
        5: "42.51", 6: "35.87", 7: "31.13", 8: "27.58",
        9: "24.82", 10: "22.61", 11: "20.81", 12: "19.31",
      },
      pix: "197",
    },
  },
};

const formatCPF = (value) =>
  value
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    .substring(0, 14);

// Brazilian phone mask: (XX) XXXX-XXXX (landline) or (XX) XXXXX-XXXX (mobile).
// Caps at 11 digits, so no extra digits are accepted once complete.
const formatPhone = (value) => {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const formatExpiry = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const getCardBrand = (number) => {
  const digits = onlyDigits(number);
  if (/^4/.test(digits)) return "visa";
  if (
    /^(5[1-5])/.test(digits) ||
    /^(222[1-9]|22[3-9]|2[3-6]|27[01]|2720)/.test(digits)
  )
    return "mastercard";
  if (
    /^(4011|4312|4389|4514|4576|5041|5066|5067|5090|6277|6362|6363)/.test(digits)
  )
    return "elo";
  return null;
};

const pushEvent = (event, value) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    purchase_value: value,
    currency: "BRL",
  });
};

const CheckoutSingle = ({ isMobile }) => {
  const query = useQuery();
  const codeFromQuery = query.get("o") ?? "DEFAULT";
  const origin = query.get("utm_source") ?? "";

  const [offer, setOffer] = useState(null);
  const [isOfferLoading, setIsOfferLoading] = useState(false);
  const period = normalizeOfferPeriod(offer?.period);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");

  const [paymentMethod, setPaymentMethod] = useState("credit_card");

  const [card_name, setCard_name] = useState("");
  const [card_number, setCard_number] = useState("");
  const [card_expiry, setCard_expiry] = useState("");
  const [card_cvv, setCard_cvv] = useState("");
  const [n_inst, setN_inst] = useState("");

  const [cep, setCep] = useState("");
  const [endereco, setEndereco] = useState({
    rua: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [isEmailAlreadyUsed, setIsEmailAlreadyUsed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // screen: 'form' | 'pix' | 'end'
  const [screen, setScreen] = useState("form");
  const [payStatus, setPayStatus] = useState("PENDING");
  const [pix_code, setPix_code] = useState("");
  const [password, setPassword] = useState("");
  const [subscription_id, setSubscription_id] = useState(null);

  const personalEventFired = useRef(false);
  const initiateEventFired = useRef(false);
  const checkoutStepsLogged = useRef({});
  const { getIdToken } = useAuth();

  const logCheckoutStepOnce = (eventName) => {
    if (checkoutStepsLogged.current[eventName]) return;
    checkoutStepsLogged.current[eventName] = true;
    logCheckoutAnytime(eventName);
  };

  const logCheckoutAnytime = (eventName) => {
    if (!eventName) return;
    const cookieId = Cookies.get("uid");
    (async () => {
      let token = null;
      try {
        token = await getIdToken();
      } catch (_) {
        token = null;
      }
      logCheckoutEvent({
        eventName,
        cookieId,
        token,
        fullUrl: window.location.href,
      });
    })();
  };

  const logCheckoutApiError = (error, method, route) => {
    const status = error?.status || 0;
    logCheckoutAnytime(`checkout_api_${status}_${method}_${route}`);
  };

  // ---------- Land at the top when arriving on the checkout page ----------
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ---------- Offer loading ----------
  useEffect(() => {
    async function fetchOffer() {
      if (CACHED_OFFERS[codeFromQuery]) {
        setOffer(CACHED_OFFERS[codeFromQuery]);
        return;
      }
      try {
        setIsOfferLoading(true);
        const response = await fetch(
          `${baseAPI_URL}/offers/get-offer/${codeFromQuery}`,
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );
        const result = await response.json();
        if (!response.ok) {
          logCheckoutAnytime(
            `checkout_api_${response.status}_GET_/offers/get-offer/${codeFromQuery}`
          );
        }
        setOffer(response.ok ? result : null);
      } catch (error) {
        console.error("Error getting offer:", error);
        logCheckoutAnytime(`checkout_api_0_GET_/offers/get-offer/${codeFromQuery}`);
        setOffer(null);
      } finally {
        setIsOfferLoading(false);
      }
    }
    fetchOffer();
  }, [codeFromQuery]);

  // ---------- PERSONAL event on arrival ----------
  useEffect(() => {
    if (offer && !personalEventFired.current) {
      personalEventFired.current = true;
      pushEvent("manual_event_PERSONAL", offer.prices.base);
      notify(
        "CHECKOUT: personal",
        `Usuário chegou ao checkout. isMobile: ${isMobile}`
      );
    }
  }, [offer, isMobile]);

  // ---------- INITIATE_CHECKOUT + lead capture once contact data is valid ----------
  useEffect(() => {
    if (!offer || initiateEventFired.current) return;

    const personalInvalid = validatePersonalFields({ name, email, phone });
    const isPersonalValid =
      !personalInvalid.name && !personalInvalid.email && !personalInvalid.phone;

    if (!isPersonalValid) return;

    initiateEventFired.current = true;
    pushEvent("manual_event_INITIATE_CHECKOUT", offer.prices.base);
    notify(
      "CHECKOUT: initiate",
      `Usuário ${email} preencheu os dados de contato.`
    );

    createInitiateCheckoutLead({ name, email, phone: onlyDigits(phone) }).catch((error) => {
      const message =
        error?.data?.message || "Este email já possui uma conta.";
      setIsEmailAlreadyUsed(true);
      setFieldErrors((prev) => ({ ...prev, email: true }));
      setGlobalError(message);
      logCheckoutAnytime("checkout_validation_email_already_used");
      logCheckoutApiError(error, "POST", "/email/leads");
      notify(
        "CHECKOUT: personal",
        `Problema no checkout: ${message} (email: ${email})`
      );
    });
  }, [name, email, phone, offer]);

  useEffect(() => {
    if (offer) {
      logCheckoutStepOnce("checkout_enter");
    }
  }, [offer]);

  useEffect(() => {
    if (!offer) return;
    const personalInvalid = validatePersonalFields({ name, email, phone });
    const isPersonalValid =
      !personalInvalid.name && !personalInvalid.email && !personalInvalid.phone;
    if (isPersonalValid) {
      logCheckoutStepOnce("checkout_fill_personal_info");
    }
  }, [name, email, phone, offer]);

  useEffect(() => {
    if (isValidCPF(cpf)) {
      logCheckoutStepOnce("checkout_fill_cpf");
    }
  }, [cpf]);

  useEffect(() => {
    const cardFilled =
      Boolean(card_name.trim()) &&
      onlyDigits(card_number).length >= 16 &&
      card_expiry.replace(/\D/g, "").length >= 4 &&
      String(card_cvv).length >= 3;
    if (cardFilled) {
      logCheckoutStepOnce("checkout_fill_card_info");
    }
  }, [card_name, card_number, card_expiry, card_cvv]);

  useEffect(() => {
    if (screen === "pix") {
      logCheckoutStepOnce("checkout_generate_pix");
    }
    if (screen === "pix" || screen === "end") {
      logCheckoutStepOnce("checkout_pay");
    }
  }, [screen]);

  // ---------- CEP autocomplete ----------
  const handleCepChange = (e) => {
    const value = e.target.value;
    setCep(value);

    if (value.length === 9 && value.includes("-")) {
      buscarEndereco(value);
    } else if (value.length === 8 && !value.includes("-")) {
      const formatted = `${value.slice(0, 5)}-${value.slice(5)}`;
      setCep(formatted);
      buscarEndereco(formatted);
    }
  };

  const buscarEndereco = async (cepValue) => {
    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cepValue}/json/`
      );
      const data = await response.json();
      if (!data.erro) {
        setEndereco((prev) => ({
          ...prev,
          rua: data.logradouro || "",
          bairro: data.bairro || "",
          cidade: data.localidade || "",
          estado: data.uf || "",
        }));
        setFieldErrors((prev) => ({
          ...prev,
          cep: false,
          rua: false,
          bairro: false,
          cidade: false,
          estado: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching CEP:", error);
    }
  };

  const clearError = (field) =>
    setFieldErrors((prev) => ({ ...prev, [field]: false }));

  // ---------- Submit ----------
  const handleCardNumberChange = (e) => {
    const value = onlyDigits(e.target.value).slice(0, 16);
    setCard_number(value.replace(/(.{4})/g, "$1 ").trim());
    clearError("card_number");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setGlobalError("");

    const { errors, isValid } = validateCheckout({
      name,
      email,
      phone,
      cpf,
      paymentMethod,
      card_name,
      card_number,
      card_expiry,
      card_cvv,
      n_inst,
      cep,
      endereco,
    });

    setFieldErrors(errors);

    if (!isValid) {
      setGlobalError("Confira os campos destacados em vermelho.");
      Object.keys(errors).forEach((field) => {
        logCheckoutAnytime(`checkout_validation_${field}`);
      });
      return;
    }

    if (isEmailAlreadyUsed) {
      setGlobalError(
        "Este email já possui uma conta. Faça login para continuar."
      );
      logCheckoutAnytime("checkout_validation_email_already_used");
      return;
    }

    setIsSubmitting(true);

    if (paymentMethod === "credit_card") {
      await handleCardPayment();
    } else {
      await handlePixPayment();
    }
  };

  const handleCardPayment = async () => {
    pushEvent("manual_event_CPF", offer.prices.pix);
    pushEvent("manual_event_ADDRESSINFO", offer.prices.base);

    const { month, year } = parseExpiry(card_expiry) || {};

    try {
      const result = await createCardSubscription({
        name,
        cpf,
        email,
        phone: onlyDigits(phone),
        period,
        offer,
        n_inst,
        cep,
        endereco,
        card_name,
        card_number,
        card_month: month,
        card_year: year,
        card_cvv,
        origin,
      });

      if (result.status === "CONFIRMED") {
        setPayStatus("CONFIRMED");
        setPassword(result.initialPassword);
        trackMetaPurchase(
          result.subscriptionId ?? result.subscription_id,
          offer.prices.credit_card?.[n_inst] ?? offer.prices.base
        );
        notify(
          "CHECKOUT: card",
          `Usuário ${email} finalizou a compra no cartão com sucesso.`
        );
        setScreen("end");
      }
    } catch (error) {
      const message =
        error?.data?.message || "Ocorreu um erro ao processar o pagamento.";
      setGlobalError(message);
      logCheckoutApiError(error, "POST", "/new_subscriptions/create-credit-card-subscription");
      notify(
        "CHECKOUT: ERRO",
        `Problema no checkout: usuário ${email} teve este erro no cartão: ${message}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePixPayment = async () => {
    pushEvent("manual_event_CPF", offer.prices.pix);

    try {
      const start = performance.now();
      const result_pix = await createPixSubscription1({
        name,
        cpf,
        email,
        phone: onlyDigits(phone),
        period,
        offer,
        origin,
      });
      const elapsedMs = performance.now() - start;

      setPix_code(result_pix.pixPaymentCode);
      setIsSubmitting(false);
      setScreen("pix");
      pushEvent("manual_event_PIX", offer.prices.pix);
      notify(
        "CHECKOUT: pix",
        `Usuário ${email} gerou o código pix. Tempo (1): ${elapsedMs.toFixed(0)} ms.`
      );

      // Second (background) call, after the pix code is already displayed.
      const result_subscription = await createPixSubscription2({
        name,
        cpf,
        email,
        phone: onlyDigits(phone),
        period,
        offer,
        subscription: result_pix.subscription,
        payments: result_pix.payments,
        origin,
      });

      setPassword(result_subscription.initialPassword);
      setSubscription_id(result_subscription.subscriptionId);
      const elapsedMs2 = performance.now() - start;
      notify(
        "CHECKOUT: pix",
        `Assinatura pix criada para ${email}. Tempo total: ${elapsedMs2.toFixed(0)} ms. ID: ${result_subscription.subscriptionId}`
      );
    } catch (error) {
      const message =
        error?.data?.message || "Ocorreu um erro ao gerar o Pix.";
      setGlobalError(message);
      setScreen("form");
      logCheckoutApiError(error, "POST", "/new_subscriptions/create-pix-subscription1");
      notify(
        "CHECKOUT: ERRO",
        `Problema no checkout (pix): usuário ${email} teve este erro: ${message}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------- Render guards ----------
  if (isOfferLoading) {
    return <div className="cks-loading">Carregando oferta...</div>;
  }
  if (!offer) {
    return <div className="cks-loading">Oferta não encontrada.</div>;
  }

  if (screen === "pix") {
    return (
      <div className="cks-page cks-page-centered">
        <PixScreen
          email={email}
          value={offer.prices.pix}
          pix_code={pix_code}
          subscription_id={subscription_id}
          onConfirmed={() => {
            setPayStatus("CONFIRMED");
            setScreen("end");
          }}
          onChangeMethod={() => {
            setPaymentMethod("credit_card");
            setScreen("form");
          }}
        />
      </div>
    );
  }

  if (screen === "end") {
    return (
      <div className="cks-page cks-page-centered">
        <SuccessScreen
          payStatus={payStatus}
          name={name}
          email={email}
          password={password}
        />
      </div>
    );
  }

  const cardBrand = getCardBrand(card_number);

  const inst_options = Array.from({ length: 12 }, (_, i) => {
    const installment = i + 1;
    const label = offer.prices.credit_card[installment];
    return installment === 1
      ? `${installment}x de R$ ${label} (à vista)`
      : `${installment}x de R$ ${label}*`;
  });

  return (
    <div className="cks-page">
      {isSubmitting && (
        <div
          className="cks-submit-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="cks-submit-overlay-content">
            <span className="cks-submit-spinner" aria-hidden="true" />
            <p className="cks-submit-overlay-message">
              {paymentMethod === "pix"
                ? "Gerando o seu código pix..."
                : "Processando o seu pagamento com cartão de crédito..."}
            </p>
          </div>
        </div>
      )}

      <div className="cks-layout">
        {isMobile && (
          <OfferSummary offer={offer} isMobile={true} defaultOpen={false} />
        )}

        <form className="cks-form" onSubmit={handleSubmit} noValidate>
          {/* ---------- Personal data ---------- */}
          <section className="cks-section">
            <h2 className="cks-section-title">
              <span className="cks-step-num">1</span> Seus dados
            </h2>

            <div className="cks-fields">
              <TextField
                label="Nome completo"
                variant="outlined"
                fullWidth
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  clearError("name");
                }}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name ? "Informe seu nome" : " "}
              />

              <TextField
                label="E-mail"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value.trim());
                  clearError("email");
                  setIsEmailAlreadyUsed(false);
                }}
                error={!!fieldErrors.email}
                helperText={
                  fieldErrors.email
                    ? isEmailAlreadyUsed
                      ? "Este email já possui conta"
                      : "E-mail inválido"
                    : " "
                }
              />

              <TextField
                label="Celular com DDD"
                variant="outlined"
                fullWidth
                value={phone}
                onChange={(e) => {
                  setPhone(formatPhone(e.target.value));
                  clearError("phone");
                }}
                inputProps={{ inputMode: "numeric", maxLength: 15 }}
                error={!!fieldErrors.phone}
                helperText={fieldErrors.phone ? "Telefone inválido" : " "}
              />

              <TextField
                label="CPF"
                variant="outlined"
                fullWidth
                value={cpf}
                onChange={(e) => {
                  setCpf(formatCPF(e.target.value));
                  clearError("cpf");
                }}
                inputProps={{ maxLength: 14 }}
                error={!!fieldErrors.cpf}
                helperText={fieldErrors.cpf ? "CPF inválido" : " "}
              />
            </div>
          </section>

          {/* ---------- Payment ---------- */}
          <section className="cks-section">
            <h2 className="cks-section-title">
              <span className="cks-step-num">2</span> Forma de pagamento
            </h2>

            {/* Credit card method */}
            <div
              className={`cks-method ${
                paymentMethod === "credit_card" ? "cks-active" : ""
              }`}
            >
              <label className="cks-method-header">
                <Radio
                  checked={paymentMethod === "credit_card"}
                  onChange={() => setPaymentMethod("credit_card")}
                  value="credit_card"
                  color="success"
                />
                <CreditCard size={20} className="cks-method-icon" />
                <span className="cks-method-label">Cartão de crédito</span>
                <span className="cks-method-tag">até 12x</span>
              </label>

              {paymentMethod === "credit_card" && (
                <div className="cks-method-body">
                  <TextField
                    label="Nome impresso no cartão"
                    fullWidth
                    value={card_name}
                    onChange={(e) => {
                      setCard_name(e.target.value);
                      clearError("card_name");
                    }}
                    placeholder="Ex: JOÃO A S SILVA"
                    error={!!fieldErrors.card_name}
                    helperText={fieldErrors.card_name ? "Informe o nome do cartão" : " "}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <User className="cks-input-icon" size={18} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Número do cartão"
                    fullWidth
                    value={card_number}
                    onChange={handleCardNumberChange}
                    placeholder="0000 0000 0000 0000"
                    inputProps={{ inputMode: "numeric", maxLength: 19 }}
                    error={!!fieldErrors.card_number}
                    helperText={fieldErrors.card_number ? "Número de cartão inválido" : " "}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {cardBrand ? (
                            <img
                              src={`/${cardBrand}.png`}
                              alt={cardBrand}
                              className="cks-card-brand"
                            />
                          ) : (
                            <CreditCard className="cks-input-icon" size={18} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Validade (MM/AA)"
                        fullWidth
                        value={card_expiry}
                        onChange={(e) => {
                          setCard_expiry(formatExpiry(e.target.value));
                          clearError("card_expiry");
                        }}
                        placeholder="MM/AA"
                        inputProps={{ inputMode: "numeric", maxLength: 5 }}
                        error={!!fieldErrors.card_expiry}
                        helperText={fieldErrors.card_expiry ? "Validade inválida" : " "}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="CVV"
                        fullWidth
                        value={card_cvv}
                        onChange={(e) => {
                          setCard_cvv(onlyDigits(e.target.value).slice(0, 4));
                          clearError("card_cvv");
                        }}
                        placeholder="3 ou 4 dígitos"
                        inputProps={{ inputMode: "numeric", maxLength: 4 }}
                        error={!!fieldErrors.card_cvv}
                        helperText={fieldErrors.card_cvv ? "CVV inválido" : " "}
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    select
                    label="Parcelamento"
                    fullWidth
                    value={n_inst}
                    onChange={(e) => {
                      setN_inst(e.target.value);
                      clearError("n_inst");
                    }}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                    error={!!fieldErrors.n_inst}
                    helperText={
                      fieldErrors.n_inst
                        ? "Escolha o número de parcelas"
                        : `* juros de ${offer.interest_rate * 100}% ao mês`
                    }
                  >
                    <MenuItem value="" disabled>
                      Escolha o número de parcelas...
                    </MenuItem>
                    {inst_options.map((option, i) => (
                      <MenuItem key={i} value={i + 1}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Address (CEP last visible field, autocompletes the rest) */}
                  <div className="cks-address">
                    <h3 className="cks-subtitle">Endereço de cobrança</h3>

                    <TextField
                      label="CEP"
                      fullWidth
                      value={cep}
                      onChange={handleCepChange}
                      inputProps={{ maxLength: 9 }}
                      error={!!fieldErrors.cep}
                      helperText={
                        fieldErrors.cep
                          ? "CEP inválido"
                          : "Preencheremos o endereço automaticamente"
                      }
                    />

                    {cep.length === 9 && (
                      <div className="cks-address-expanded">
                        <Grid container spacing={2}>
                          <Grid item xs={8}>
                            <TextField
                              label="Rua"
                              fullWidth
                              value={endereco.rua}
                              onChange={(e) => {
                                setEndereco((p) => ({ ...p, rua: e.target.value }));
                                clearError("rua");
                              }}
                              error={!!fieldErrors.rua}
                              helperText={fieldErrors.rua ? "Informe a rua" : " "}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              label="Número"
                              fullWidth
                              value={endereco.numero}
                              onChange={(e) => {
                                setEndereco((p) => ({
                                  ...p,
                                  numero: e.target.value,
                                }));
                                clearError("numero");
                              }}
                              error={!!fieldErrors.numero}
                              helperText={fieldErrors.numero ? "Nº" : " "}
                            />
                          </Grid>
                        </Grid>

                        <TextField
                          label="Complemento (opcional)"
                          fullWidth
                          value={endereco.complemento}
                          onChange={(e) =>
                            setEndereco((p) => ({
                              ...p,
                              complemento: e.target.value,
                            }))
                          }
                          helperText=" "
                        />

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              label="Bairro"
                              fullWidth
                              value={endereco.bairro}
                              onChange={(e) => {
                                setEndereco((p) => ({
                                  ...p,
                                  bairro: e.target.value,
                                }));
                                clearError("bairro");
                              }}
                              error={!!fieldErrors.bairro}
                              helperText={fieldErrors.bairro ? "Informe o bairro" : " "}
                            />
                          </Grid>
                          <Grid item xs={8} sm={5}>
                            <TextField
                              label="Cidade"
                              fullWidth
                              value={endereco.cidade}
                              onChange={(e) => {
                                setEndereco((p) => ({
                                  ...p,
                                  cidade: e.target.value,
                                }));
                                clearError("cidade");
                              }}
                              error={!!fieldErrors.cidade}
                              helperText={fieldErrors.cidade ? "Informe a cidade" : " "}
                            />
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <TextField
                              label="UF"
                              fullWidth
                              value={endereco.estado}
                              onChange={(e) => {
                                setEndereco((p) => ({
                                  ...p,
                                  estado: e.target.value,
                                }));
                                clearError("estado");
                              }}
                              inputProps={{ maxLength: 2 }}
                              error={!!fieldErrors.estado}
                              helperText={fieldErrors.estado ? "UF" : " "}
                            />
                          </Grid>
                        </Grid>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Pix method */}
            <div
              className={`cks-method ${
                paymentMethod === "pix" ? "cks-active" : ""
              }`}
            >
              <label className="cks-method-header">
                <Radio
                  checked={paymentMethod === "pix"}
                  onChange={() => setPaymentMethod("pix")}
                  value="pix"
                  color="success"
                />
                <QrCode size={20} className="cks-method-icon" />
                <span className="cks-method-label">Pix</span>
                <span className="cks-method-tag cks-method-tag-green">
                  à vista
                </span>
              </label>

              {paymentMethod === "pix" && (
                <div className="cks-method-body">
                  <div className="cks-pix-info">
                    Ao finalizar, um <strong>código Pix</strong> será gerado e
                    exibido na próxima tela. Basta copiá-lo e pagar no app do seu
                    banco — o acesso é liberado automaticamente após a
                    confirmação.
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ---------- Summary + CTA ---------- */}
          <div className="cks-order-summary">
            <span>Total</span>
            <strong>
              {paymentMethod === "pix"
                ? `R$ ${offer.prices.pix} à vista no Pix`
                : n_inst
                ? `${n_inst}x de R$ ${offer.prices.credit_card[n_inst]}`
                : `12x de R$ ${offer.prices.credit_card[12]}`}
            </strong>
          </div>

          {globalError && (
            <div className="cks-global-error">
              {globalError}{" "}
              {isEmailAlreadyUsed && <a href="/logon">Fazer login</a>}
            </div>
          )}

          <button
            type="submit"
            className="cks-submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Processando..."
              : paymentMethod === "pix"
              ? "Gerar Pix e finalizar"
              : "Comprar agora"}
          </button>

          <div className="cks-secure">
            <Lock size={14} /> Ambiente seguro • Pagamento criptografado SSL
          </div>

          <div className="cks-powered-by">
            <span>Pagamento processado por</span>
            <img src="/Logo_Asaas2026.png" alt="Asaas" />
          </div>
        </form>

        {!isMobile && <OfferSummary offer={offer} isMobile={false} />}
      </div>
    </div>
  );
};

export default CheckoutSingle;
